import { useState } from 'react';
import { MerchandisingRuleTypes } from 'src/services';
import { getConditionFieldCategory, SUBTYPE_OPTIONS_BY_CATEGORY } from './helpers';
import { ConditionFieldOption } from './types';

type SubTypeOption =
  (typeof SUBTYPE_OPTIONS_BY_CATEGORY)[keyof typeof SUBTYPE_OPTIONS_BY_CATEGORY][number];

export interface ConditionFieldState {
  field: string;
  subType: MerchandisingRuleTypes.MerchandisingRuleSubType | '';
  value: string;
  selectedField: ConditionFieldOption | undefined;
  category: ReturnType<typeof getConditionFieldCategory>;
  subTypeOptions: SubTypeOption[];
  setValue: (value: string) => void;
  setSubType: (subType: MerchandisingRuleTypes.MerchandisingRuleSubType) => void;
  handleFieldChange: (nextField: string) => void;
  reset: (
    field: string,
    subType: MerchandisingRuleTypes.MerchandisingRuleSubType | '',
    value: string
  ) => void;
}

// Shared field/condition/value picker state used by both the "New rule" composer and the
// "Edit rule" drawer - keeps the field->category->subtype-options derivation and the
// field-change reset behavior (clearing subtype/value) in one place.
export function useConditionField(
  fieldOptions: ConditionFieldOption[],
  initial: {
    field: string;
    subType: MerchandisingRuleTypes.MerchandisingRuleSubType | '';
    value: string;
  }
): ConditionFieldState {
  const [field, setField] = useState(initial.field);
  const [subType, setSubType] = useState<MerchandisingRuleTypes.MerchandisingRuleSubType | ''>(
    initial.subType
  );
  const [value, setValue] = useState(initial.value);

  const selectedField = fieldOptions.find(option => option.value === field);
  const category = selectedField ? getConditionFieldCategory(selectedField.types) : 'unsupported';
  const subTypeOptions = SUBTYPE_OPTIONS_BY_CATEGORY[category];

  const handleFieldChange = (nextField: string): void => {
    setField(nextField);
    const nextCategory = getConditionFieldCategory(
      fieldOptions.find(option => option.value === nextField)?.types ?? []
    );
    setSubType(SUBTYPE_OPTIONS_BY_CATEGORY[nextCategory][0]?.value ?? '');
    setValue('');
  };

  const reset = (
    nextField: string,
    nextSubType: MerchandisingRuleTypes.MerchandisingRuleSubType | '',
    nextValue: string
  ): void => {
    setField(nextField);
    setSubType(nextSubType);
    setValue(nextValue);
  };

  return {
    field,
    subType,
    value,
    selectedField,
    category,
    subTypeOptions,
    setValue,
    setSubType,
    handleFieldChange,
    reset,
  };
}
